import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Landmark, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { useAccounting } from '../hooks/useAccounting';
import { localDateStr } from '../utils/customers';
import { money } from '../utils/quotes';
import { downloadCsv } from '../utils/sales';
import {
  addMonths, cashBalanceUntil, cashMovements, cashToCsv, expensesToCsv, monthKey, monthLabel, monthRange, monthSeries, monthShort, monthSummary, monthlyToCsv,
  payablesSummary, receivablesTotal
} from '../utils/accounting';

const tone = (n) => (n < 0 ? 'text-rose-300' : 'text-emerald-400');

function Kpi({ label, value, hint, color = 'text-seryi-heading' }) {
  return (
    <div className="bg-seryi-bg border border-seryi-bg-light rounded-2xl p-4">
      <p className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">{label}</p>
      <p className={`text-xl font-black ${color}`}>{money(value)}</p>
      {hint && <p className="text-[10px] text-seryi-text mt-0.5">{hint}</p>}
    </div>
  );
}

function ByList({ title, rows, empty }) {
  return (
    <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-2">
      <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">{title}</h3>
      {rows.length === 0 ? <p className="text-xs text-seryi-text">{empty}</p> : rows.map((r) => (
        <div key={r.key} className="space-y-0.5">
          <div className="flex justify-between text-xs text-seryi-heading"><span>{r.label}</span><span className="font-bold">{money(r.amount)}{r.share !== undefined ? <span className="text-seryi-text font-normal"> · {Math.round(r.share * 100)}%</span> : null}</span></div>
          {r.share !== undefined && <div className="h-1.5 rounded-full bg-seryi-bg-dark overflow-hidden"><div className="h-full bg-rose-400/70" style={{ width: `${Math.max(2, Math.round(r.share * 100))}%` }} /></div>}
        </div>
      ))}
    </div>
  );
}

export default function CashPage() {
  const data = useAccounting();
  const { sales, payments, expenses, loading, tableMissing } = data;
  const current = monthKey(localDateStr());
  const [month, setMonth] = useState(current);

  const first = useMemo(() => {
    const days = [...sales.map((s) => s.created_at), ...payments.map((p) => p.paid_at), ...expenses.map((e) => e.expense_date)].filter(Boolean).map((d) => String(d).slice(0, 7));
    return days.length ? days.sort()[0] : current;
  }, [sales, payments, expenses, current]);

  const summary = useMemo(() => monthSummary(data, month), [data, month]);
  const series = useMemo(() => monthSeries(data, month, 6), [data, month]);
  const payables = useMemo(() => payablesSummary(expenses), [expenses]);
  const receivable = useMemo(() => receivablesTotal(sales.filter((s) => s.status === 'por_cobrar' || s.status === 'parcial')), [sales]);
  const balance = useMemo(() => cashBalanceUntil(data, month), [data, month]);
  const salesById = useMemo(() => new Map(sales.map((s) => [s.id, s])), [sales]);

  // Meses elegibles: desde el primer movimiento hasta el actual (nunca más de 36).
  const options = useMemo(() => {
    const list = [];
    for (let ym = current; ym >= first && list.length < 36; ym = addMonths(ym, -1)) list.push(ym);
    return list;
  }, [current, first]);

  const exportMonthly = () => downloadCsv(`resultado_mensual_${month}.csv`, monthlyToCsv(monthSeries(data, month, 12)));
  const exportCash = () => downloadCsv(`caja_${month}.csv`, cashToCsv(cashMovements({ payments, expenses, salesById }, month)));
  const exportExpenses = () => {
    const { from, nextFrom } = monthRange(month);
    downloadCsv(`gastos_${month}.csv`, expensesToCsv(expenses.filter((e) => e.expense_date >= from && e.expense_date < nextFrom)));
  };

  if (tableMissing) {
    return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261006b_expenses.sql</code> en Supabase para usar la caja y el resultado.</div>;
  }

  return (
    <div className="space-y-5 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2"><Landmark size={20} /> Caja y resultado</h2>
          <p className="text-xs text-seryi-text">Cuánto vendiste (ventas, pedidos y citas), cuánto cobraste, cuánto gastaste y cuánto te queda. Los gastos se registran en <Link to="/v2/gastos" className="text-seryi-green hover:underline">Gastos y por pagar</Link>.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" aria-label="Mes anterior" disabled={month <= first} onClick={() => setMonth(addMonths(month, -1))} className="p-2 border border-seryi-bg-light rounded-xl text-seryi-heading disabled:opacity-40"><ChevronLeft size={14} /></button>
          <select aria-label="Mes" className="bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading capitalize" value={month} onChange={(e) => setMonth(e.target.value)}>
            {options.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
          <button type="button" aria-label="Mes siguiente" disabled={month >= current} onClick={() => setMonth(addMonths(month, 1))} className="p-2 border border-seryi-bg-light rounded-xl text-seryi-heading disabled:opacity-40"><ChevronRight size={14} /></button>
        </div>
      </div>

      {loading ? <p className="text-xs text-seryi-text">Cargando…</p> : (
        <>
          <section aria-label="Resultado del mes" className="space-y-2">
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">Resultado de {monthLabel(month)} <span className="text-seryi-text font-normal normal-case">· lo vendido menos los gastos del mes</span></h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Kpi label="Ventas" value={summary.sold} hint="Ventas no anuladas del mes" />
              <Kpi label="Gastos" value={summary.accrued} hint="Pagados y por pagar del mes" color="text-rose-300" />
              <Kpi label="Utilidad" value={summary.result} color={tone(summary.result)} />
            </div>
          </section>

          <section aria-label="Caja del mes" className="space-y-2">
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">Caja de {monthLabel(month)} <span className="text-seryi-text font-normal normal-case">· lo que entró menos lo que salió</span></h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Cobrado" value={summary.collected} hint="Abonos recibidos" color="text-emerald-400" />
              <Kpi label="Pagado" value={summary.paidOut} hint="Gastos que salieron" color="text-rose-300" />
              <Kpi label="Caja del mes" value={summary.cash} color={tone(summary.cash)} />
              <Kpi label="Saldo acumulado" value={balance} hint={`Hasta fin de ${monthLabel(month)}`} color={tone(balance)} />
            </div>
          </section>

          <section aria-label="Pendientes" className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Kpi label="Por cobrar hoy" value={receivable} hint="Saldos de ventas abiertas" color="text-amber-300" />
            <Kpi label="Por pagar hoy" value={payables.total} hint={payables.overdue ? `${money(payables.overdue)} vencido` : 'Nada vencido'} color="text-amber-300" />
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ByList title="Gastos por categoría" rows={summary.byCategory} empty="No hay gastos este mes." />
            <ByList title="Cobrado por medio de pago" rows={summary.collectedByMethod} empty="No hay cobros este mes." />
            <ByList title="Pagado por medio de pago" rows={summary.paidByMethod} empty="No hay pagos este mes." />
          </div>

          <div className="overflow-auto rounded-3xl border border-seryi-bg-light">
            <table className="w-full text-xs text-seryi-heading" aria-label="Últimos seis meses">
              <thead className="bg-seryi-bg-dark text-seryi-text text-[10px] uppercase">
                <tr><th className="p-3 text-left">Mes</th><th className="p-3 text-right">Ventas</th><th className="p-3 text-right">Gastos</th><th className="p-3 text-right">Utilidad</th><th className="p-3 text-right">Cobrado</th><th className="p-3 text-right">Pagado</th><th className="p-3 text-right">Caja</th></tr>
              </thead>
              <tbody>
                {series.map((r) => (
                  <tr key={r.month} className={`border-t border-seryi-bg-light ${r.month === month ? 'bg-seryi-green/5 font-bold' : ''}`}>
                    <td className="p-3 capitalize">{monthShort(r.month)}</td>
                    <td className="p-3 text-right">{money(r.sold)}</td><td className="p-3 text-right">{money(r.accrued)}</td>
                    <td className={`p-3 text-right ${tone(r.result)}`}>{money(r.result)}</td>
                    <td className="p-3 text-right">{money(r.collected)}</td><td className="p-3 text-right">{money(r.paidOut)}</td>
                    <td className={`p-3 text-right ${tone(r.cash)}`}>{money(r.cash)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-3">
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">Para tu contador</h3>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={exportMonthly} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5"><Download size={13} /> Resultado mensual (12 meses)</button>
              <button type="button" onClick={exportCash} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5"><Download size={13} /> Movimientos de caja de {monthLabel(month)}</button>
              <button type="button" onClick={exportExpenses} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5"><Download size={13} /> Gastos de {monthLabel(month)}</button>
            </div>
            <p className="text-[11px] text-seryi-text">Son archivos CSV que abre Excel. Es un control básico del negocio: no reemplaza la contabilidad legal ni declara impuestos.</p>
          </div>
        </>
      )}
    </div>
  );
}
